import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { company } from "@/data/content";
import { BrandLogo } from "@/components/site/BrandLogo";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Our Services" },
  { to: "/sectors", label: "Sectors" },
  { to: "/accreditations", label: "Accreditations" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
    <img src="/brand/tfs-logo-white.png" alt="Total Facilities Services" className="h-12 w-auto" />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-border" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-container mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-white" : "text-slatetxt hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-5">
          <a href={`tel:${company.phone.replace(/\s/g, "")}`} data-testid="nav-phone" className="flex items-center gap-2 text-sm text-slatetxt hover:text-white transition-colors">
            <Phone size={16} /> {company.phone}
          </a>
          <Link
            to="/quote"
            data-testid="nav-quote-button"
            className="bg-brand hover:bg-brand-hover text-white text-sm font-semibold px-5 py-2.5 transition-colors duration-300"
          >
            Request a Quote
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-ink/95 backdrop-blur-xl border-b border-border px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-base font-medium py-1 ${isActive ? "text-white" : "text-slatetxt"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/quote" data-testid="mobile-quote-button" className="mt-2 bg-brand text-white text-center font-semibold px-5 py-3">
            Request a Quote
          </Link>
        </div>
      )}
    </header>
  );
};
